import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { reservationApi } from '../../lib/api';
import toast from 'react-hot-toast';

// Status with label, color, and step number in the workflow
const STATUS: Record<string, { label: string; color: string; step: number; desc: string }> = {
  draft:              { label: '작성 중',   color: 'bg-gray-100 text-gray-600',      step: 0, desc: '아직 요청이 전송되지 않았습니다' },
  requested:          { label: '검토 중',   color: 'bg-amber-100 text-amber-700',     step: 1, desc: '호스트와 관리자가 검토하고 있습니다' },
  host_approved:      { label: '호스트 승인', color: 'bg-blue-100 text-blue-700',     step: 2, desc: '차주가 예약을 승인했습니다' },
  payment_pending:    { label: '견적 확정', color: 'bg-indigo-100 text-indigo-700',   step: 3, desc: '견적이 확정되었습니다. 세금계산서 또는 카드 등록을 진행해주세요' },
  payment_authorized: { label: '카드 등록 완료', color: 'bg-green-100 text-green-700', step: 4, desc: '카드 등록 완료. 반납 후 최종 금액이 청구됩니다.' },
  confirmed:          { label: '예약 확정', color: 'bg-emerald-100 text-emerald-700',  step: 5, desc: '예약이 확정되었습니다. 촬영 당일 차량을 인도받으세요' },
  in_progress:        { label: '대여 중',   color: 'bg-blue-100 text-blue-700',       step: 6, desc: '현재 차량을 사용 중입니다' },
  completed:          { label: '반납 완료', color: 'bg-teal-100 text-teal-700',       step: 7, desc: '차량 반납이 완료되었습니다' },
  payment_captured:   { label: '정산 완료', color: 'bg-green-100 text-green-700',     step: 8, desc: '최종 정산이 완료되었습니다' },
  settled:            { label: '종료',      color: 'bg-gray-100 text-gray-500',       step: 9, desc: '모든 절차가 완료되었습니다' },
  rejected_by_host:   { label: '호스트 거절', color: 'bg-red-100 text-red-600',       step: -1, desc: '차주가 예약을 거절했습니다' },
  rejected_by_admin:  { label: '관리자 거절', color: 'bg-red-100 text-red-600',       step: -1, desc: '관리자가 예약을 거절했습니다' },
  cancelled:          { label: '취소됨',    color: 'bg-gray-100 text-gray-400',       step: -1, desc: '예약이 취소되었습니다' },
};

const STEPS = [
  { step: 1, label: '문의 요청', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
  { step: 2, label: '호스트 검토', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { step: 3, label: '견적 확정', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { step: 4, label: '결제·세금계산서', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
  { step: 5, label: '예약 확정', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { step: 6, label: '차량 이용', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { step: 7, label: '반납 완료', icon: 'M5 13l4 4L19 7' },
  { step: 8, label: '정산 완료', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

export default function MyReservationsPage() {
  const { id } = useParams();
  const [reservations, setReservations] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reservationApi.list().then((res: any) => {
      setReservations(res.reservations || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (id) {
      reservationApi.getById(id).then((res: any) => setSelected(res)).catch(() => {});
    } else {
      setSelected(null);
    }
  }, [id]);

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';
  const formatDate = (d: string) => new Date(d).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
      </div>
    );
  }

  // Detail view
  if (selected) {
    const s = STATUS[selected.status] || { label: selected.status, color: 'bg-gray-100 text-gray-600', step: 0, desc: '' };
    const currentStep = s.step;
    const isActive = currentStep > 0;

    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => { setSelected(null); window.history.pushState({}, '', '/reservations'); }}
          className="text-sm text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          예약 목록으로
        </button>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-slate-900">예약 상세</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${s.color}`}>{s.label}</span>
          </div>

          {/* Vehicle info */}
          {selected.vehicles?.length > 0 && (
            <div className="space-y-2 mb-5">
              {selected.vehicles.map((v: any, i: number) => (
                <div key={v.id || i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-20 h-14 rounded-lg bg-gray-200 overflow-hidden shrink-0">
                    {v.primary_photo ? (
                      <img src={v.primary_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🚗</div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{v.make} {v.model}</p>
                    <p className="text-xs text-gray-500">{v.year}년식 · {v.color || '색상 미기재'} · {v.vehicle_type}</p>
                    {v.license_plate && (
                      <p className="text-xs text-gray-500">차량번호: {v.license_plate}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">{formatPrice(v.daily_rate_snapshot || v.daily_rate)}/일</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* My request notes */}
          {selected.guest_notes && (
            <div className="mb-4 bg-blue-50 rounded-xl p-3 text-sm">
              <p className="text-xs font-medium text-blue-500 mb-1">내 요청 사항</p>
              <p className="text-blue-800">{selected.guest_notes}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm mb-4">
            <div><span className="text-gray-400">인도일</span><p className="font-medium">{formatDate(selected.pickup_date)}</p></div>
            <div><span className="text-gray-400">반납일</span><p className="font-medium">{formatDate(selected.return_date)}</p></div>
            <div><span className="text-gray-400">대여 기간</span><p className="font-medium">{selected.rental_days}일</p></div>
            <div><span className="text-gray-400">예상 금액</span><p className="font-medium text-amber-700">{formatPrice(selected.total_amount)}</p></div>
          </div>

          {/* Line items */}
          {selected.line_items?.length > 0 && (
            <div className="mb-4 border border-gray-100 rounded-lg divide-y divide-gray-50 text-sm">
              {selected.line_items.map((li: any) => (
                <div key={li.id} className="flex justify-between p-2.5">
                  <span className="text-gray-600 text-xs">
                    {li.item_type === 'rental' ? '🚗 대여료' : li.item_type === 'platform_fee' ? '🏢 수수료' : li.item_type === 'care' ? '🔧 현장케어' : li.item_type === 'transport' ? '🚛 운송비' : li.item_type === 'shuttle' ? '🚐 셔틀' : '📦 기타'}
                    {li.description ? ` - ${li.description}` : ''}
                  </span>
                  <span className="font-medium text-xs">{formatPrice(li.total_amount)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Inspection photos */}
          {selected.inspection_photos?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">📸 인수인계 사진 ({selected.inspection_photos.length}장)</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {selected.inspection_photos.slice(0, 8).map((p: any) => (
                  <div key={p.id} className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                    <img src={p.photo_url} alt={p.photo_category} className="w-full h-full object-cover" />
                  </div>
                ))}
                {selected.inspection_photos.length > 8 && (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center text-xs text-gray-500 shrink-0">
                    +{selected.inspection_photos.length - 8}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Host info */}
          {selected.host && (
            <div className="mb-3 p-3 bg-gray-50 rounded-xl text-xs text-gray-500">
              <span className="font-medium text-gray-600">차주:</span> {selected.host.name} · {selected.host.phone}
            </div>
          )}

          {/* Host negotiation notes */}
          {selected.host_notes && (
            <div className="mt-3 bg-green-50 rounded-xl p-3 text-sm">
              <p className="text-xs font-medium text-green-500 mb-1">호스트 협의 내용</p>
              <p className="text-green-800">{selected.host_notes}</p>
            </div>
          )}
          {selected.admin_notes && (
            <div className="mt-3 bg-purple-50 rounded-xl p-3 text-sm">
              <p className="text-xs font-medium text-purple-500 mb-1">관리자 메모</p>
              <p className="text-purple-800">{selected.admin_notes}</p>
            </div>
          )}

          {/* Card registration CTA */}
          {selected.status === 'payment_pending' && (
            <div className="mt-4">
              <Link
                to={`/reservations/${selected.id}/payment`}
                className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                카드 등록하러 가기
              </Link>
              <p className="text-xs text-center text-gray-400 mt-2">견적이 확정되었습니다. 카드를 등록하면 승인만 보류되고 반납 후 청구됩니다.</p>
            </div>
          )}
        </div>

        {/* Progress Stepper */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-6">진행 상황</h2>
          <div className="space-y-0">
            {STEPS.map((step, i) => {
              const done = currentStep > 0 && step.step <= currentStep;
              const current = step.step === currentStep;
              const rejected = currentStep < 0;
              const isLast = i === STEPS.length - 1;

              return (
                <div key={step.step} className="flex gap-4">
                  {/* Connector line + circle */}
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      done ? 'bg-slate-900 text-white' :
                      current ? 'bg-brand-600 text-white ring-4 ring-brand-100' :
                      rejected ? 'bg-red-100 text-red-400' :
                      'bg-gray-100 text-gray-300'
                    }`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={step.icon} />
                      </svg>
                    </div>
                    {!isLast && (
                      <div className={`w-0.5 h-8 mt-1 ${done ? 'bg-slate-300' : 'bg-gray-100'}`} />
                    )}
                  </div>
                  {/* Label */}
                  <div className={`pb-6 ${!isLast ? '' : ''}`}>
                    <p className={`text-sm font-medium ${done ? 'text-slate-900' : current ? 'text-brand-700' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                    {current && (
                      <p className="text-xs text-brand-500 mt-0.5">{s.desc}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rejection message */}
          {(selected.status === 'rejected_by_host' || selected.status === 'rejected_by_admin' || selected.status === 'cancelled') && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl text-sm text-red-700">
              <p className="font-medium mb-1">
                {selected.status === 'rejected_by_host' ? '호스트가 예약을 거절했습니다' :
                 selected.status === 'rejected_by_admin' ? '관리자가 예약을 거절했습니다' :
                 '예약이 취소되었습니다'}
              </p>
              {selected.rejection_reason && <p className="text-red-500">사유: {selected.rejection_reason}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">내 예약·문의</h1>
      <p className="text-sm text-gray-500 mb-6">차량 대여 문의 및 진행 상황을 확인하세요</p>

      {reservations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <svg className="w-12 h-12 text-gray-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-400 mb-2">아직 문의 내역이 없습니다</p>
          <Link to="/" className="text-brand-600 hover:underline text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">차량 둘러보기</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map((r: any) => {
            const s = STATUS[r.status] || { label: r.status, color: 'bg-gray-100 text-gray-600', step: 0 };
            const currentStep = s.step > 0 ? s.step : 0;
            const progressPct = Math.max(0, Math.min(100, (currentStep / 8) * 100));

            return (
              <Link
                key={r.id}
                to={`/reservations/${r.id}`}
                className="block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <div className="flex items-start gap-3 mb-3">
                  {/* Vehicle thumbnail */}
                  <div className="w-14 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0 relative">
                    {r.vehicles?.[0]?.primary_photo ? (
                      <img src={r.vehicles[0].primary_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg">🚗</div>
                    )}
                    {(r.vehicles?.length || 0) > 1 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {r.vehicles.length}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">
                          {r.vehicles?.length ? (
                            r.vehicles.length === 1
                              ? `${r.vehicles[0].make} ${r.vehicles[0].model}`
                              : `${r.vehicles[0].make} ${r.vehicles[0].model} 외 ${r.vehicles.length - 1}대`
                          ) : (
                            r.vehicle_make && r.vehicle_model ? `${r.vehicle_make} ${r.vehicle_model}` : '차량 정보 없음'
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(r.pickup_date)} ~ {formatDate(r.return_date)}
                          <span className="text-gray-400 ml-1">({r.rental_days}일)</span>
                        </p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${s.color}`}>
                        {s.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mini progress bar */}
                {currentStep > 0 && currentStep <= 8 && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          currentStep >= 5 ? 'bg-emerald-500' : currentStep >= 3 ? 'bg-brand-500' : 'bg-amber-400'
                        }`}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-400">{currentStep}/8</span>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
